import { BACKUP, Backup, RESTORE, Restore } from 'app/redux/actions/persistence';

import {
  PROCESS_ENTRY, PROCESS_ENTRY_SUCCESS, ProcessEntry,
  ProcessEntrySuccess,
  REMOVE_ONE_CLICK_BET_ENTRY, RemoveOneClickBetEntry,
  UPDATE_ONE_CLICK_BET_AMOUNT_PREFERENCE,
  UpdateOneClickBetAmountPreference,
} from '../actions/oneClickBet';
import {
  SUBMIT_STAKE_FAILURE, SUBMIT_STAKE_REQUEST, SUBMIT_STAKE_SUCCESS, SubmitStakeFailure,
  SubmitStakeRequest,
  SubmitStakeSuccess,
} from '../actions/submission';
import { BetErrorCode, SubmissionState } from '../constants';
import { OneClickBetState } from '../types/oneClickBet';
import { createPreset } from '../utils/entries';
import { setupEntryError } from '../utils/stake-types-reducers';

// ---

const DEFAULT_ONE_CLICK_BET_AMOUNT = 50;

const initialState: OneClickBetState = {
  processingEntry: null,
  amount: createPreset({ value: DEFAULT_ONE_CLICK_BET_AMOUNT }),
};

type RestoreAction = Restore<{amount: OneClickBetState['amount']}>;

export type OneClickBetActions =
  | Backup
  | RestoreAction
  | UpdateOneClickBetAmountPreference
  | RemoveOneClickBetEntry
  | ProcessEntry
  | ProcessEntrySuccess
  | SubmitStakeRequest
  | SubmitStakeSuccess
  | SubmitStakeFailure;

// ---

function handleRestore(
  state: OneClickBetState,
  payload: RestoreAction['payload'],
): OneClickBetState {
  if (payload.amount) {
    state = {
      ...state,
      amount: payload.amount,
    };
  }

  return state;
}

function updateOneClickBetAmountPreference(
  state: OneClickBetState,
  { preference }: UpdateOneClickBetAmountPreference['payload'],
): OneClickBetState {
  return {
    ...state,
    amount: preference,
  };
}

// ---

function updateEntrySubmissionState(
  state: OneClickBetState,
  value: SubmissionState,
): OneClickBetState {
  const { processingEntry } = state;

  if (!processingEntry) {
    return state;
  }

  return {
    ...state,
    processingEntry: {
      ...processingEntry,
      betInfo: processingEntry.betInfo.set('submission', value),
    },
  };
}

function handleSubmitStakeFailure(
  state: OneClickBetState,
  error: SubmitStakeFailure['error'],
  payload: SubmitStakeFailure['payload'],
): OneClickBetState {
  const { processingEntry } = state;

  if (!processingEntry) {
    return state;
  }

  let bet = processingEntry.bet;

  if (payload.betErrors) {
    const error = payload.betErrors[0];

    if (error.code === BetErrorCode.PRICE_CHANGED && error.newPrice && bet) {
      bet = bet.updateIn(['entity', 'price'], () => error.newPrice);
    }
  }

  return {
    ...state,
    processingEntry: {
      ...processingEntry,
      bet,
      betInfo: setupEntryError(processingEntry.betInfo, error, payload),
    },
  };
}

// ---

export default function (
  state = initialState,
  action: OneClickBetActions,
): OneClickBetState {
  switch (action.type) {
    case BACKUP:
      return { amount: state.amount } as OneClickBetState;

    case RESTORE:
      return handleRestore(state, action.payload);

    case PROCESS_ENTRY:
      return {
        ...state,
        processingEntry: action.payload,
      };

    case PROCESS_ENTRY_SUCCESS:
    case REMOVE_ONE_CLICK_BET_ENTRY:
      return {
        ...state,
        processingEntry: null,
      };

    case UPDATE_ONE_CLICK_BET_AMOUNT_PREFERENCE:
      return updateOneClickBetAmountPreference(state, action.payload);

    case SUBMIT_STAKE_REQUEST:
      return updateEntrySubmissionState(state, SubmissionState.PROCESSING);

    case SUBMIT_STAKE_SUCCESS: {
      if (action.payload.isBigStake) {
        return state;
      }
      return updateEntrySubmissionState(state, SubmissionState.ACCEPTED);
    }

    case SUBMIT_STAKE_FAILURE: {
      if (action.payload.isBigStake) {
        return state;
      }
      return handleSubmitStakeFailure(state, action.error, action.payload);
    }

    default:
      return state;
  }
}
